import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getUsers, deleteUser } from '../../../services/users';
import DataTable from '../../../components/Tables/DataTable';
import Toast from '../../../components/Common/Toast';
import { 
  UserPlus, 
  Search, 
  Eye, 
  Edit3, 
  Trash2, 
  Mail, 
  Phone,
  UserCircle
} from 'lucide-react';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to load user directory' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
      try {
        await deleteUser(id);
        setToast({ type: 'success', message: 'User removed successfully' });
        loadUsers();
      } catch (err) {
        setToast({ type: 'error', message: err.response?.data?.error || 'Delete failed' });
      }
    }
  };

  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { 
      key: 'full_name', 
      label: 'User',
      render: (value, row) => (
        <div className="flex items-center gap-3 py-1">
          <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs">
            {value?.charAt(0).toUpperCase() || <UserCircle size={20} />}
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-slate-900">{value}</span>
            <span className="text-[10px] text-slate-400 uppercase font-medium tracking-wider">{row.role?.replace('_', ' ')}</span>
          </div>
        </div>
      )
    },
    { 
      key: 'email', 
      label: 'Contact Information',
      render: (value, row) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Mail size={12} className="text-slate-300" /> {value}
          </div>
          {row.phone && (
            <div className="flex items-center gap-2 text-xs text-slate-400 italic">
              <Phone size={12} className="text-slate-300" /> {row.phone}
            </div>
          )}
        </div>
      )
    },
    { 
      key: 'is_active', 
      label: 'Status', 
      render: (val) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${
          val 
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
            : 'bg-rose-50 text-rose-700 border-rose-200'
        }`}>
          {val ? 'Active' : 'Suspended'}
        </span>
      ) 
    },
    {
      key: 'actions',
      label: 'Management',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Link 
            to={`/dashboard/org-admin/users/${row.id}`}
            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye size={18} />
          </Link>
          <Link 
            to={`/dashboard/org-admin/users/${row.id}/edit`}
            className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
            title="Edit User"
          >
            <Edit3 size={18} />
          </Link>
          <button 
            onClick={() => handleDelete(row.id, row.full_name)}
            className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
            title="Delete User"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      {/* HEADER AREA */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-slate-100 pb-8">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Users</h1>
          <p className="text-slate-500 font-medium mt-1 italic">Manage team access and profile permissions.</p>
        </div>
        <Link to="/dashboard/org-admin/users/new">
          <button className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3.5 rounded-2xl font-bold transition-all shadow-xl shadow-indigo-100 active:scale-95">
            <UserPlus size={20} />
            Create User
          </button>
        </Link>
      </div>

      {/* SEARCH TOOLBAR */}
      <div className="flex items-center bg-white p-2 rounded-2xl border border-slate-200 shadow-sm max-w-md group focus-within:ring-2 focus-within:ring-indigo-500/20 transition-all">
        <div className="pl-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors">
          <Search size={20} />
        </div>
        <input 
          type="text"
          placeholder="Filter by name or email..."
          className="w-full p-3 border-none bg-transparent focus:ring-0 text-sm placeholder:italic"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* DATA TABLE */}
      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-100/50 overflow-hidden">
        <DataTable columns={columns} data={filteredUsers} loading={loading} />
        
        {!loading && filteredUsers.length === 0 && (
          <div className="py-20 text-center">
            <div className="h-16 w-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
              <Search size={32} />
            </div>
            <p className="text-slate-500 font-medium italic">No users found matching your criteria.</p>
          </div>
        )}
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default UserList;